<template>
  <div>
    <p class='mb-5'>
      Met onderstaand formulier kunt u een wijziging aanbrengen in uw eerder aangemaakte ziekmelding.
      Wanneer u de wijziging aan de ziekmelding heeft ingediend, krijgt u van ons bericht per e-mail.
      Ook kunt u de status van uw claim inzien op de verzuim pagina van deze portal.
    </p>
    <employee-card :employee="this.employee" showPolicies :policyCoverageDate="editReportOfSickness.firstOfAbsence" class='mt-5 mb-4' />

    <InfoContainer :multipleMessages="firstOfAbsenceWarnings" v-if="firstOfAbsenceWarnings.length > 0" class="mt-3 mb-3" />
    <error-container v-if="(isOnlyPoliciesBefore || isOnlyParticipationsBefore) && !isLastClosedReportOfSicknessSmallerThanMonthAgo" class='mt-4 mb-4'>
      <div v-if="isOnlyPoliciesBefore">
        <h6>Geen dekking doordat de polis was/is beëindigd door de werkgever.</h6>
        <p>Er bestaat geen dekking meer op deze polis(sen) op de door u gekozen datum van ziekmelding</p>
      </div>

      <div v-if="isOnlyParticipationsBefore" :class="isOnlyPoliciesBefore ? 'mt-2' : ''">
        <h6>Geen dekking door beeindigde deelnames</h6>
        <p>{{ 'Voor ' + employee.getFullName() + ' bestaat er geen dekking op de door u gekozen datum en kan er geen ziekmelding geregistreerd worden.' }}</p>
      </div>
    </error-container>

    <form-input-container
      title='Invoer Wijziging Verzuim'
      text='De door u ingevoerde gegevens voor het wijzigen van de verzuimmelding'>

      <edit-report-of-sickness-confirmation-box :reportOfSickness="this.reportOfSickness" />
    </form-input-container>

    <div style='float: right' class='mt-4'>
      <Button primary class='mr-3' @click="cancel(views.Edit)">
        {{ 'buttons.previous-step' | translate }}
      </Button>

      <Button positive
        :disabled="!isValid || isUpdateLoading || isCurrentlyProcessing"
        @click="confirm(editReportOfSickness)">
        {{ buttonText | translate}}
      </Button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import ERS from '@/views/constants/report-of-sickness-edit';
import ReportsOfSickness from '@/resources/constants/reports-of-sickness';

import EditReportOfSicknessConfirmationBox from './EditReportOfSicknessConfirmationBox.vue';
import EmployeeCard from '@/views/components/employees/EmployeeCard.vue';

import FormInputContainer from '@aevitae/aevitaeui/0_atoms/containers/FormInputContainer.vue';
import Button from '@aevitae/aevitaeui/0_atoms/buttons/Button.vue';
import InfoContainer from '@aevitae/aevitaeui/1_molecules/containers/InfoContainer.vue';
import ErrorContainer from '@aevitae/aevitaeui/0_atoms/containers/ErrorContainer.vue';

import Helpers from '@/shared/helpers/functions';
import Compositions from '@/views/helpers/compositions';
import PolicyDates from '@/views/helpers/policy-dates';

export default {
  name: 'EditReportOfSicknessConfirmation',
  components: {
    EmployeeCard,
    EditReportOfSicknessConfirmationBox,
    FormInputContainer,
    Button,
    InfoContainer,
    ErrorContainer
  },
  props: {
    reportOfSickness: Object
  },
  data () {
    return {
      views: ERS.Views
    };
  },
  computed: {
    ...mapGetters({
      editReportOfSickness: 'editReportOfSickness',
      requiredFields: 'editReportOfSicknessRequiredFields',
      validations: 'editReportOfSicknessValidations',
      isUpdateLoading: 'isUpdateReportOfSicknessLoading',

      reportsOfSickness: 'reportsOfSickness',
      isCurrentlyProcessing: 'isCurrentlyProcessing'
    }),
    employee () {
      return this.$store.getters.employees.find(employee => employee.id === this.reportOfSickness.employeeId);
    },
    isValid () {
      return this.validations.isValid() && this.requiredFields.isValid() &&
        ((!this.isOnlyPoliciesBefore && !this.isOnlyParticipationsBefore) || this.isLastClosedReportOfSicknessSmallerThanMonthAgo);
    },
    buttonText () {
      if (this.isUpdateLoading) {
        return 'report-of-sickness.form.updating-report-of-sickness';
      } else {
        return 'report-of-sickness.form.confirm-and-create-report-of-sickness';
      }
    },

    employeePolicies () {
      return Compositions.getPoliciesFromParticipations(
        this.employee.allParticipations,
        this.$store.getters.policies
      );
    },

    employeeProductTypes () {
      return _.uniq(this.employeePolicies.map(p => p.productType));
    },

    policyDates () {
      return new PolicyDates(
        this.employee.allParticipations,
        this.$store.getters.policies,
        this.editReportOfSickness.firstOfAbsence
      ).getAll();
    },

    shouldShowWIAFortyTwoWeeksWarning () {
      if (this.employeeProductTypes.indexOf('WIA') > -1) {
        if (this.editReportOfSickness.firstOfAbsence) {
          return Helpers.Dates.differenceFromNowInWeeks(this.editReportOfSickness.firstOfAbsence) >= 42;
        }
      }
      return false;
    },

    /* TO-DO:
       Dit klopt niet voor FirstOfAbsence. activePolicies??
       alles ophalen voor datum..
    */
    noCoveragePolicies () {
      let participations = this.employee.allParticipations.filter(p => p.isActiveWaiverOnDate(this.policyCoverageDate));
      let result = Compositions.getPoliciesFromParticipations(participations, this.$store.getters.activePolicies);
      return result.map(p => p.product.description).join(',  ');
    },

    isOnlyPoliciesBefore () {
      const policiesAfter = this.policyDates.policies.after.length > 0;
      const policiesBefore = this.policyDates.policies.before.length > 0;
      const activePolicies = this.policyDates.policies.active.length > 0;

      return policiesBefore && !policiesAfter && !activePolicies;
    },

    isOnlyParticipationsBefore () {
      const participationsAfter = this.policyDates.participations.after.length > 0;
      const participationsBefore = this.policyDates.participations.before.length > 0;
      const activeParticipations = this.policyDates.participations.length > 0;

      return participationsBefore && !participationsAfter && !activeParticipations;
    },

    isLastClosedReportOfSicknessSmallerThanMonthAgo () {
      if (this.reportsOfSickness.hasOwnProperty(this.employee.id)) {
        return Compositions.isFirstOfAbsenceWithinBufferLastReport(
          this.reportsOfSickness[this.employee.id].filter(r => r.id !== this.editReportOfSickness.id), this.editReportOfSickness.firstOfAbsence);
      }
      return false;
    },

    firstOfAbsenceWarnings () {
      let result = [];
      if (this.policyDates.policies.after.length > 0) {
        result.push({
          title: 'Geen dekking door polis nog niet gestart voor werkgever',
          text: 'Op [[firstOfAbsence]] waren de volgende polissen nog niet van start gegaan: [[policies]].'
            .replace('[[policies]]', this.policyDates.policies.after.map(p => p.product.description).join(', '))
            .replace('[[firstOfAbsence]]', Helpers.Dates.formatDate(this.editReportOfSickness.firstOfAbsence))
        });
      }

      if (this.policyDates.participations.after.length > 0) {
        const _policies = Compositions.getPoliciesFromParticipations(
          this.policyDates.participations.after,
          this.employeePolicies
        );

        result.push({
          title: 'Geen dekking doordat deelname nog niet van start is gegaan.',
          text: '[[employee]] Nam op [[firstOfAbsence]] nog niet deel aan de volgende polissen: [[policies]].'
            .replace('[[employee]]', this.employee.getFullName())
            .replace('[[policies]]', _policies.map(p => p.product.description).join(', '))
            .replace('[[firstOfAbsence]]', Helpers.Dates.formatDate(this.editReportOfSickness.firstOfAbsence))
        });
      }

      if (this.shouldShowWIAFortyTwoWeeksWarning) {
        result.push({
          title: 'Eerste verzuimdag is langer dan 42 weken geleden.',
          text: 'De eerste verzuimdag  is  langer dan 42 weken geleden, de datum wordt geregistreerd. Wij zullen binnenkort contact met u opnemen voor nadere informatie.'
        });
      }

      if (this.noCoveragePolicies !== '') {
        result.push({
          title: 'Voor de volgende polissen geldt een afstandsverklaring',
          text: `Let op: de volgende polissen hebben geen dekking op ${this.formatDate(this.editReportOfSickness.firstOfAbsence)}: ${this.noCoveragePolicies}`
        });
      }

      return result;
    }
  },
  methods: {
    ...mapActions({
      confirm: ReportsOfSickness.Actions.Update.START,
      cancel: ERS.Actions.Navigate.DO
    })
  }
};
</script>

<style>
</style>
